// @file Photo-booth-specific state for the combined AV recorder.
// Extracted from useContentPickerAvRecorderStore to keep concerns separated.
// Dependencies: useGlobalContentPickerStore, useGlobalConfirmationDialogStore (no dependency on the AV store).

import { ALERT_ICON } from '@@/bits/confirmation_dialog'
import { ContentPickerSource } from '@@/bits/content_picker'
import { PhotoBoothStatus, type PhotoBoothPickerState } from '@@/bits/content_picker_configs'
import { __ } from '@@/bits/intl'
import { unboundedWatch } from '@@/bits/vue'
import {
  OzConfirmationDialogBoxButtonScheme,
  useGlobalConfirmationDialogStore,
} from '@@/pinia/global_confirmation_dialog'
import { useGlobalContentPickerStore } from '@@/pinia/useGlobalContentPickerStore'
import { defineStore } from 'pinia'
import { computed } from 'vue'

const useContentPickerAvPhotoBoothStore = defineStore('contentPickerAvPhotoBoothStore', () => {
  const globalStore = useGlobalContentPickerStore()
  const globalConfirmationDialogStore = useGlobalConfirmationDialogStore()

  // #region Photo booth state

  function setPhotoBoothStatus(value: PhotoBoothStatus): void {
    globalStore.setPickerState(ContentPickerSource.PhotoBooth, {
      source: ContentPickerSource.PhotoBooth,
      photoBoothStatus: value,
    })
  }

  const photoBoothStatus = computed<PhotoBoothStatus>(() => {
    const state = globalStore.currentSession?.pickerStates[ContentPickerSource.PhotoBooth]
    if (state != null && 'photoBoothStatus' in state) {
      return (state as PhotoBoothPickerState).photoBoothStatus
    }
    return PhotoBoothStatus.Connecting
  })

  const hasUnsavedPhotoWork = computed<boolean>(() => {
    return photoBoothStatus.value === PhotoBoothStatus.Captured
  })

  const hasStartedPhotoCapture = computed<boolean>(() => {
    const s = photoBoothStatus.value
    return s === PhotoBoothStatus.Capturing || s === PhotoBoothStatus.Captured
  })

  // #endregion Photo booth state

  // #region Close guard

  function showDiscardDialog(): void {
    globalConfirmationDialogStore.openConfirmationDialog({
      ...ALERT_ICON,
      title: __('Delete photo?'),
      body: __('Deleting a photo is permanent and cannot be undone.'),
      confirmButtonText: __('Delete'),
      cancelButtonText: __('Nevermind'),
      afterConfirmActions: [() => setPhotoBoothStatus(PhotoBoothStatus.Streaming)],
      afterCancelActions: [],
      shouldFadeIn: false,
      forceFullWidthButtons: true,
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
    })
  }

  // #endregion Close guard

  // #region Beforeunload
  // Watcher needed because the beforeunload listener must be managed at the store level
  // (not the component) to survive component unmount/remount cycles during capture.
  // A computed property cannot produce side effects (adding/removing event listeners).

  const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
    if (hasUnsavedPhotoWork.value) {
      event.preventDefault()
    }
  }

  unboundedWatch(hasUnsavedPhotoWork, (hasWork) => {
    if (hasWork) {
      window.addEventListener('beforeunload', handleBeforeUnload)
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  })

  // #endregion Beforeunload

  return {
    photoBoothStatus,
    setPhotoBoothStatus,
    hasUnsavedPhotoWork,
    hasStartedPhotoCapture,
    showDiscardDialog,
  }
})

export { useContentPickerAvPhotoBoothStore }
